import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { AppDataService, RiderService } from 'app/services';
import { AppComponent } from 'app/app.component';
import { MessageService, MenuItem } from 'primeng/api';
import { RiderUploadDocModel, RiderVehicleTypeEnum } from 'app/models';
import { SignupParams } from 'app/components/operator/operator-signup/signup-params';
import * as moment from 'moment';
import { XpressDocumentTypeEnum } from '@shared/enums/xpress-document-type.enum';

enum MenuTab {
    Identification = 1,
    VehicleDocuments = 2,
    TNVSDocuments = 3
}

type UploadFieldName =
    | 'DriverProfessionalLicenceFrontImageUrl'
    | 'DriverProfessionalLicenceBackImageUrl'
    | 'DriverNBIClearanceImageUrl'
    | 'RidesharingAppImageUrl'
    | 'LTOOfficialReceiptImageUrl'
    | 'CertificateRegistrationImageUrl'
    | 'SPAImageUrl'
    | 'AuthorizationLetterImageUrl'
    | 'CertificatePublicConvenienceImageUrl'
    | 'ProvisionalAuthorityImageUrl';

@Component({
    selector: 'driver-upload-doc-dialog',
    templateUrl: './driver-upload-doc-dialog.component.html',
    styleUrls: ['./driver-upload-doc-dialog.component.scss',
        '../../../../../../styles/menu-sidebar.scss',
        '../../../../../../styles/style-layout.scss'
    ]
})
export class DriverUploadDocDialogComponent implements OnInit {
    public visibleDriverUploadDoc: boolean = false;
    @Input()
    set _visibleDriverUploadDoc(value: boolean) {
        this.visibleDriverUploadDoc = value;
    }

    public riderId: number = 0;
    @Input()
    set _riderId(value: number) {
        this.riderId = value;
        this.riderUploadDocModel.RiderId = value;
    }

    public xpressUserId: number = 0;
    @Input()
    set _xpressUserId(value: number) {
        this.xpressUserId = value;
    }

    public vehicleType!: RiderVehicleTypeEnum;
    @Input()
    set _vehicleType(value: RiderVehicleTypeEnum) {
        this.vehicleType = value;
    }

    @Output() changedVisibleDriverUploadDocEmit = new EventEmitter<boolean>();
    changedVisibleDriverUploadDoc(increased: any) {
        this.changedVisibleDriverUploadDocEmit.emit(increased);
    }

    constructor(private messageService: MessageService, public appData: AppDataService,
        public app: AppComponent, private riderService: RiderService) { }

    public menuTab = MenuTab;
    public activeTab: MenuTab = MenuTab.Identification;
    public menuItems: MenuItem[] = [];

    public riderUploadDocModel: RiderUploadDocModel = new RiderUploadDocModel();

    public riderVehicleTypeEnum = RiderVehicleTypeEnum;

	public isLoading: boolean = false;

	public minDate: Date = new Date();
    private uploadedDocumentMeta: Partial<Record<UploadFieldName, { id: number; type: XpressDocumentTypeEnum }>> = {};
    private readonly fieldTypeMap: Record<UploadFieldName, XpressDocumentTypeEnum> = {
        DriverProfessionalLicenceFrontImageUrl: XpressDocumentTypeEnum.DriverLicense,
        DriverProfessionalLicenceBackImageUrl: XpressDocumentTypeEnum.DriverLicense,
        DriverNBIClearanceImageUrl: XpressDocumentTypeEnum.NBIClearance,
        RidesharingAppImageUrl: XpressDocumentTypeEnum.PreviousAppExperience,
        LTOOfficialReceiptImageUrl: XpressDocumentTypeEnum.OfficialReceiptAndCertificateOfRegistration,
        CertificateRegistrationImageUrl: XpressDocumentTypeEnum.OfficialReceiptAndCertificateOfRegistration,
        SPAImageUrl: XpressDocumentTypeEnum.NotarizedSPA,
        AuthorizationLetterImageUrl: XpressDocumentTypeEnum.AuthorizationLetter,
        CertificatePublicConvenienceImageUrl: XpressDocumentTypeEnum.Cpc,
        ProvisionalAuthorityImageUrl: XpressDocumentTypeEnum.Pa
    };

    ngOnInit(): void {
        this.getMenuItems();
    }

    private getMenuItems() {
        if (this.vehicleType === this.riderVehicleTypeEnum.Sedan ||
            this.vehicleType === this.riderVehicleTypeEnum.SUV) {
            this.menuItems.push(
                {
                    label: 'Identification',
                    command: (e: any) => this.handleTabChange(MenuTab.Identification)
                },
                {
                    label: 'Vehicle Documents',
                    command: (e: any) => this.handleTabChange(MenuTab.VehicleDocuments)
                },
                {
                    label: 'TNVS Documents',
                    command: (e: any) => this.handleTabChange(MenuTab.TNVSDocuments)
                }
            );
        } else {
            this.menuItems.push(
                {
                    label: 'Identification',
                    command: (e: any) => this.handleTabChange(MenuTab.Identification)
                },
                {
                    label: 'Vehicle Documents',
                    command: (e: any) => this.handleTabChange(MenuTab.VehicleDocuments)
                }
            );
        }
    }

    public handleTabChange(menuTab: MenuTab) {
        this.activeTab = menuTab;
    }

    public cancel() {
        this.changedVisibleDriverUploadDoc(false);
    }

    public checkFieldsForDisabledBtn(): boolean {
        switch (this.activeTab) {
            case MenuTab.Identification:
                return !this.riderUploadDocModel.IsIdentificationFieldsFilled();
            case MenuTab.VehicleDocuments:
                return !this.riderUploadDocModel.IsVehicleDocumentsFieldsFilled();
            case MenuTab.TNVSDocuments:
                return !this.riderUploadDocModel.IsTNVCDocumentsFieldsFilled();
            default: return true;
        }
    }

    fileBrowseHandler(event: any, fieldName: UploadFieldName) {
        const files = event?.target?.files ? event.target.files : event;
        if (files.length === 1) {
            const file = files[0];
            if (file.size > SignupParams.MAX_UPLOAD_FILE_SIZE) {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'The file size exceeds 5 MB' });
                return;
            }
            else if (!SignupParams.ACCEPTED_FILE_TYPES.includes(file.type)) {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Unsupported file format. Please upload a JPEG, PNG, or PDF file' });
                return;
            }

            (this.riderUploadDocModel as any)[fieldName + 'File'] = file;
            this.uploadDocument(file, fieldName);
        }
        else {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'You are uploading more than 1 image' });
        }
    }

    private uploadDocument(file: File, fieldName: UploadFieldName) {
        const formData = new FormData();
        const documentType = this.fieldTypeMap[fieldName];
        formData.append('File', file);
        formData.append('Type', String(documentType));
        if (this.xpressUserId > 0) {
            formData.append('XpressUserId', String(this.xpressUserId));
        }

        (this.riderUploadDocModel as any)[fieldName + 'IsLoading'] = true;

        this.riderService.uploadImage(formData, this.messageService)
            .then(data => {
                if (!data?.Success) {
                    (this.riderUploadDocModel as any)[fieldName + 'IsLoading'] = false;
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: data?.ErrorMessage || 'Failed to upload document.'
                    });
                    return;
                }

                (this.riderUploadDocModel as any)[fieldName] = data.FileUrl;
                this.uploadedDocumentMeta[fieldName] = {
                    id: data.Id,
                    type: data.Type
                };
                (this.riderUploadDocModel as any)[fieldName + 'IsLoading'] = false;
            })
            .catch(err => {
                (this.riderUploadDocModel as any)[fieldName + 'IsLoading'] = false;
                throw err;
            });
    }

    public deleteUpload(fieldName: UploadFieldName) {
        const uploadedMeta = this.uploadedDocumentMeta[fieldName];
        if (!uploadedMeta) {
            this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Unable to delete: document id is missing.' });
            return;
        }

        (this.riderUploadDocModel as any)[fieldName + 'IsLoading'] = true;

        this.riderService.deleteImage({
            Id: uploadedMeta.id,
            Type: uploadedMeta.type,
            XpressUserId: this.xpressUserId > 0 ? this.xpressUserId : null
        }, this.messageService)
            .then(data => {
                if (!data?.Success) {
                    (this.riderUploadDocModel as any)[fieldName + 'IsLoading'] = false;
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: data?.ErrorMessage || 'Failed to delete document.'
                    });
                    return;
                }

                (this.riderUploadDocModel as any)[fieldName] = null;
                (this.riderUploadDocModel as any)[fieldName + 'IsLoading'] = false;
                delete this.uploadedDocumentMeta[fieldName];
            })
            .catch(err => {
                (this.riderUploadDocModel as any)[fieldName + 'IsLoading'] = false;
                throw err;
            });
    }

    public updateLegalDocuments() {
		this.isLoading = true;

        const expiryItems: any[] = [];
        if (this.riderUploadDocModel.DriverProfessionalLicenceExpiryAt) {
            expiryItems.push({
                Types: [
                    XpressDocumentTypeEnum.DriverLicense,
                    XpressDocumentTypeEnum.DriverLicenseProfessional,
                    XpressDocumentTypeEnum.DriverLicenseNonProfessional
                ],
                ExpiryDate: moment(this.riderUploadDocModel.DriverProfessionalLicenceExpiryAt).format('YYYY-MM-DD')
            });
        }
        if (this.riderUploadDocModel.DriverNBIClearanceExpiryAt) {
            expiryItems.push({
                Types: [XpressDocumentTypeEnum.NBIClearance],
                ExpiryDate: moment(this.riderUploadDocModel.DriverNBIClearanceExpiryAt).format('YYYY-MM-DD')
            });
        }
        if (this.riderUploadDocModel.LTOOfficialReceiptExpireAt) {
            expiryItems.push({
                Types: [XpressDocumentTypeEnum.OfficialReceiptAndCertificateOfRegistration],
                ExpiryDate: moment(this.riderUploadDocModel.LTOOfficialReceiptExpireAt).format('YYYY-MM-DD')
            });
        }
        if (this.riderUploadDocModel.CertificatePublicConvenienceExpireAt) {
            expiryItems.push({
                Types: [XpressDocumentTypeEnum.Cpc],
                ExpiryDate: moment(this.riderUploadDocModel.CertificatePublicConvenienceExpireAt).format('YYYY-MM-DD')
            });
        }
        if (this.riderUploadDocModel.ProvisionalAuthorityExpireAt) {
            expiryItems.push({
                Types: [XpressDocumentTypeEnum.Pa],
                ExpiryDate: moment(this.riderUploadDocModel.ProvisionalAuthorityExpireAt).format('YYYY-MM-DD')
            });
        }

        const updateExpiryPromise = expiryItems.length > 0
            ? this.riderService.updateDocumentExpiryDates({
                XpressUserId: this.xpressUserId > 0 ? this.xpressUserId : null,
                Items: expiryItems
            }, this.messageService)
            : Promise.resolve({ Success: true });

        updateExpiryPromise
            .then(data => {
                if (!data.Success) {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    this.isLoading = false;
                    return;
                }

                if (!this.riderUploadDocModel.PaOrCpcCaseNumber) {
                    this.isLoading = false;
                    this.cancel();
                    return;
                }

                this.riderService.updateTASNumber({
                    RiderId: this.riderId,
                    TASNumber: this.riderUploadDocModel.PaOrCpcCaseNumber
                }, this.messageService)
                    .then(tasResponse => {
                        this.isLoading = false;
                        if (!tasResponse.Success) {
                            this.messageService.add({ severity: 'error', summary: 'Error', detail: tasResponse.ErrorMessage });
                            return;
                        }
                        this.cancel();
                    })
                    .catch(err => {
                        this.isLoading = false;
                        throw err;
                    });
            })
            .catch(err => {
                this.isLoading = false;
                throw err;
            });
    }
}
